import React, { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { updateLanguage, updateTheme } from "redux/reducers/global/actions";
import Routes from "./routes";

/* Languages */
import ca_translations from "./translations/ca.json";
import en_translations from "./translations/en.json";
import es_translations from "./translations/es.json";
import nl_translations from "./translations/nl.json";
import ro_translations from "./translations/ro.json";

/* Opensans font */
import "@fontsource/open-sans";

/* Styles */
import "@fortawesome/fontawesome-pro/css/all.min.css";
import "styles/styles.scss";

/* AgGrid */
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import Keycloak from "keycloak-js";
import IdGisHierarchiProvider from "storage/context/id-gis-hierarchi-context";
import { TenantType } from "types/generic/tenant";

import { ReactKeycloakProvider } from "@react-keycloak/web";
import { useAppConfig } from "redux/reducers/global/hooks/use-app-config";

LicenseManager.setLicenseKey(
    "CompanyName=GLOBAL OMNIUM IDRICA, S.L.U.,LicensedGroup=GoAigua,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=2,LicensedProductionInstancesCount=1,AssetReference=AG-019519,ExpiryDate=28_September_2022_[v2]_MTY2NDMxOTYwMDAwMA==7ba61952332694c12810eba00b412622"
);

type AppType = {
    language: string;
    theme: "light" | "dark" | "mm";
    logged: boolean;
    userRoles: Array<string>;
    onUpdateLanguage: (language: string) => void;
    onUpdateTheme: (theme: "light" | "dark" | "mm") => void;
};

type TranslationType = {
    [key: string]: JsonType;
};

type JsonType = {
    [key: string]: string;
};

type keyCloakSettingsType = {
    authClient: Keycloak.KeycloakInstance;
    initOptions: {
        onLoad: string;
        checkLoginIframe: boolean;
    };
};

const App = ({
    language,
    theme,
    logged,
    userRoles,
    onUpdateLanguage,
    onUpdateTheme,
}: AppType) => {
    const [keyCloakSettings, setKeyCloakSettings] =
        useState<keyCloakSettingsType | null>(null);
    const [storedTenant, setStoredTenant] = useState<TenantType | null>(null);

    const [translations] = useState<TranslationType>({
        es: es_translations,
        ca: ca_translations,
        en: en_translations,
        ro: ro_translations,
        nl: nl_translations,
        "en-GB": en_translations,
    });

    const { setCurrentTenant, getKeycloakSettings } = useAppConfig();
    const currentWindow = window;
    setCurrentTenant({ tenant: storedTenant });

    useEffect(() => {
        onUpdateLanguage(language);
        onUpdateTheme(theme);
    }, [language, theme, onUpdateLanguage, onUpdateTheme]);

    useEffect(() => {
        (async () => {
            await getKeycloakSettings(
                setStoredTenant,
                setKeyCloakSettings,
                currentWindow
            );
        })();
    }, [currentWindow, getKeycloakSettings]);

    if (!keyCloakSettings || !storedTenant) {
        //TODO: add loading component
        return <div>loading...</div>;
    }
    return (
        translations && (
            <React.Fragment>
                {" "}
                {keyCloakSettings && storedTenant && (
                    <ReactKeycloakProvider {...keyCloakSettings}>
                        <IntlProvider
                            key={language}
                            locale={language}
                            defaultLocale={language}
                            messages={translations[language]}
                            onError={(err) => {
                                if (err.code === "MISSING_TRANSLATION") {
                                    return;
                                }
                                throw err;
                            }}
                        >
                            <IdGisHierarchiProvider>
                                <Routes logged={logged} userRoles={userRoles} />
                            </IdGisHierarchiProvider>
                        </IntlProvider>
                    </ReactKeycloakProvider>
                )}
            </React.Fragment>
        )
    );
};

const mapStateToProps = (state: any) => {
    return {
        language: state.app.language,
        theme: state.app.theme,
        logged: state.keycloak.logged,
        userRoles: state.keycloak.userRoles,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        onUpdateLanguage: (language: string) =>
            dispatch(updateLanguage(language)),
        onUpdateTheme: (theme: "light" | "dark" | "mm") =>
            dispatch(updateTheme(theme)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
